import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { loginSuccess } from "./store/authSlice";
import MainLayout from "./components/layouts/MainLayout";
import ErrorBoundary from "./components/ErrorBoundary";

// Theme
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "./theme";

// MUI components for Suspense fallback
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

// Language
import { LanguageProvider } from "./utils/LanguageContext";

// Date picker components
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

// Lazy-loaded page components
const Welcome = React.lazy(() => import("./pages/Welcome/Welcome"));
const VoterLogin = React.lazy(() => import("./pages/Verify/VoterLogin"));
const OtpEntry = React.lazy(() => import("./components/Auth/OtpEntry"));
const VoterIdVerification = React.lazy(() => import("./components/Auth/VoterIdVerification"));
const Dashboard = React.lazy(() => import("./pages/Dashboard/Dashboard"));
const VotingPage = React.lazy(() => import("./pages/Ballot/VotingPage"));
const VoterRegistration = React.lazy(() => import("./pages/Verify/VoterRegistration"));
const ScanID = React.lazy(() => import("./pages/Verify/ScanID"));
const RegistrationFlowRouter = React.lazy(() => import("./pages/Verify/RegistrationFlowRouter"));
const LoginFlowRouter = React.lazy(() => import("./pages/Verify/LoginFlowRouter"));
const MyElections = React.lazy(() => import("./pages/Elections/MyElections"));
const ElectionDashboard = React.lazy(() => import("./pages/Elections/ElectionDashboard"));
const ElectionDetails = React.lazy(() => import("./pages/Elections/ElectionDetails"));
const ElectionResults = React.lazy(() => import("./pages/Elections/ElectionResults"));
const ManageVoters = React.lazy(() => import("./pages/Elections/ManageVoters"));
const BallotBuilder = React.lazy(() => import("./pages/Elections/BallotBuilder"));
const QuickBallotBuilder = React.lazy(() => import("./pages/Elections/QuickBallotBuilder"));
const BallotDebug = React.lazy(() => import("./pages/Elections/BallotDebug"));
const PaymentSuccess = React.lazy(() => import("./pages/PaymentSuccess"));
const CheckoutCancel = React.lazy(() => import("./pages/CheckoutCancel"));
const AccessKeyRegistration = React.lazy(() => import("./pages/voter/AccessKeyRegistration"));
const RegistrationSuccess = React.lazy(() => import("./components/RegistrationSuccess"));
const BlinkIDTest = React.lazy(() => import("./components/BlinkIDTest"));

// Wrapper component for ScanID route
const ScanIDWrapper = () => {
  const navigate = useNavigate();
  const { id, slug } = useParams();

  return (
    <ScanID
      onComplete={(idData) => {
        const voterToken = localStorage.getItem("voterToken");
        const voterUser = localStorage.getItem("voterUser");
        const isFromVoterLogin = voterToken || voterUser;

        if (isFromVoterLogin) {
          navigate(`/verify-login/${id}/${slug}`, {
            state: { fromVoterLogin: true, idData },
          });
        } else {
          navigate(`/verify-registration/${id}/${slug}`, {
            state: { fromScan: true, idData },
          });
        }
      }}
      onBack={() => navigate(`/verify-identity/${id}/${slug}`)}
    />
  );
};

// Redirect components for legacy routes
const LegacyPreregisterRedirect = () => {
  const { id, slug } = useParams();
  return <Navigate to={`/verify-registration/${id}/${slug}`} replace />;
};

const LegacyVerifyIdentityRedirect = () => {
  const { id, slug } = useParams();
  return <Navigate to={`/verify-registration/${id}/${slug}`} replace />;
};

const LegacyLoginPreregisterRedirect = () => {
  const { id, slug } = useParams();
  return <Navigate to={`/verify-login/${id}/${slug}`} replace />;
};

function App() {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  // On mount: restore session from localStorage if available
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const userStr = localStorage.getItem("adminUser");
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        dispatch(loginSuccess({ user, token }));
      } catch {
        // Invalid stored data, ignore
      }
    }
  }, [dispatch]);

  return (
    <LanguageProvider>
    <ThemeProvider theme={theme}>
    <CssBaseline />
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ErrorBoundary>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <CircularProgress />
          </Box>
        }>
        <Routes>
          {/* Welcome / Entry page */}
          <Route
            path="/"
            element={isAuthenticated ? <Navigate to="/admin/my-elections" replace /> : <Navigate to="/welcome" replace />}
          />
          <Route
            path="/welcome"
            element={isAuthenticated ? <Navigate to="/admin/my-elections" replace /> : <Welcome />}
          />

          {/* Auth routes redirect to welcome in demo mode */}
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/admin/my-elections" replace /> : <Navigate to="/welcome" replace />}
          />
          <Route
            path="/register"
            element={isAuthenticated ? <Navigate to="/admin/my-elections" replace /> : <Navigate to="/welcome" replace />}
          />
          <Route path="/voter/login" element={<VoterLogin />} />
          <Route path="/verify-otp" element={<OtpEntry />} />
          <Route path="/verify-voter-id" element={<VoterIdVerification />} />

          {/* Voter Flow Routes */}
          <Route path="/vote/:id/:slug" element={<VotingPage />} />
          <Route
            path="/voter-registration/:id/:slug"
            element={<VoterRegistration />}
          />
          <Route
            path="/voter/register/:id/:slug"
            element={<VoterRegistration />}
          />

          {/* New Dedicated Verified Routes */}
          <Route
            path="/verify-registration/:id/:slug"
            element={<RegistrationFlowRouter />}
          />
          <Route path="/verify-login/:id/:slug" element={<LoginFlowRouter />} />

          {/* Legacy Routes */}
          <Route
            path="/preregister/:id/:slug"
            element={<LegacyPreregisterRedirect />}
          />
          <Route
            path="/verify-identity/:id/:slug"
            element={<LegacyVerifyIdentityRedirect />}
          />
          <Route
            path="/login/preregister/:id/:slug"
            element={<LegacyLoginPreregisterRedirect />}
          />
          <Route path="/scan-id/:id/:slug" element={<ScanIDWrapper />} />

          {/* Public Election Results Route */}
          <Route path="/elections/:id/results" element={<ElectionResults />} />

          {/* BlinkID Test Route */}
          <Route path="/blinkid-test" element={<BlinkIDTest />} />

          {/* Registration Success Route */}
          <Route
            path="/registration-success"
            element={<RegistrationSuccess />}
          />

          {/* Payment Success Route */}
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />

          {/* Access key routes */}
          <Route
            path="/ballot/access/:accessKey"
            element={<AccessKeyRegistration />}
          />
          <Route path="/ballot/key" element={<AccessKeyRegistration />} />

          {/* Admin (Election Host) Routes - Protected by Authentication */}
          <Route
            path="/admin"
            element={
              isAuthenticated ? <MainLayout /> : <Navigate to="/welcome" />
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="my-elections" element={<MyElections />} />
            <Route path="elections/:id" element={<ElectionDashboard />} />
            <Route path="elections/:id/details" element={<ElectionDetails />} />
            <Route path="elections/:id/voters" element={<ManageVoters />} />
            <Route path="elections/:id/results" element={<ElectionResults />} />
            <Route path="create-election" element={<BallotBuilder />} />
            <Route
              path="create-quick-ballot"
              element={<QuickBallotBuilder />}
            />
            <Route path="ballot-builder" element={<BallotBuilder />} />
            <Route path="elections/:id/debug" element={<BallotDebug />} />
          </Route>
        </Routes>
        </Suspense>
      </Router>
      </ErrorBoundary>
    </LocalizationProvider>
    </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
